import React, { useRef, useEffect } from "react";
import { Table, Button, Col, Row, Card } from "react-bootstrap";
import { format } from "date-fns";
import STRINGS from "../Constants/Strings";

export default function MembersApplicationSearch({
  applicationId,
  timeframe,
  fromDate,
  toDate,
  applicationStatus,
  applicationSearchResults = [],
  applicationSearchLoading = false,
  applicationSearchPagination,
  hasApplicationSearched = false,
  setHasApplicationSearched,
  applicationSortConfig,
  applicationSearchError = "",
  handleApplicationIdChange,
  handleApplicationKeyDown,
  handleTimeframeChange,
  handleApplicationSearch,
  setApplicationStatus,
  setApplicationSearchPagination,
  handleApplicationSort,
  handleViewMember,
  showTableOnly = false,
}) {
  const formatApplicationDateTime = (timestamp) => {
    if (!timestamp) return "";
    return format(new Date(timestamp), "hh:mm:ss a - MM/dd/yyyy");
  };

  const getApplicationStatus = (result) => {
    if (result.status === "Inactive")
      return { status: "Inactive", className: "text-info" };
    if (result.inReviewAt)
      return { status: "In Review", className: "text-warning" };
    if (result.uploadedAt)
      return { status: "Completed", className: "text-success" };
    return { status: "In Progress", className: "text-primary" };
  };

  const ApplicationSortableHeader = ({ field, label }) => {
    if (!handleApplicationSort) return <th>{label}</th>;

    const isSorted = applicationSortConfig?.sortBy === field;
    const direction = isSorted
      ? applicationSortConfig.sortDirection === "ASC"
        ? "Up"
        : "Down"
      : null;

    return (
      <th
        onClick={() => handleApplicationSort(field)}
        style={{ cursor: "pointer", userSelect: "none" }}
      >
        {label}
        {direction && (
          <span style={{ marginLeft: 5, fontSize: "0.8em" }}>{direction}</span>
        )}
        {!direction && (
          <i
            className="fas fa-sort"
            style={{ marginLeft: 5, fontSize: "10px", opacity: 0.4 }}
          ></i>
        )}
      </th>
    );
  };

  const handleReset = (e) => {
    e.preventDefault();
    handleApplicationIdChange({ target: { value: "" } });
    handleTimeframeChange({ target: { value: 1 } });
    setHasApplicationSearched(false);
    if (applicationFormRef.current) {
      applicationFormRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleApplicationSearch();
  };

  const applicationTableHeaderRef = useRef(null);
  const applicationFormRef = useRef(null);

  useEffect(() => {
    if (
      hasApplicationSearched &&
      applicationTableHeaderRef.current &&
      !applicationSearchLoading
    ) {
      const rootFontSize =
        parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      const stickyHeaderHeightEm = 16;
      const offset = stickyHeaderHeightEm * rootFontSize;

      const headerRect = applicationTableHeaderRef.current.getBoundingClientRect();
      const top = headerRect.top + window.scrollY - offset;

      window.scrollTo({
        top,
        behavior: "smooth",
      });
    }
  }, [hasApplicationSearched, applicationSearchLoading]);

  const timeframeLabelMap = {
    "1":
      STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS.FORM
        .QUICK_TIMEFRAME_OPTION_VALUE_0,
    "7":
      STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS.FORM
        .QUICK_TIMEFRAME_OPTION_VALUE_1,
    "30":
      STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS.FORM
        .QUICK_TIMEFRAME_OPTION_VALUE_2,
    "90":
      STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS.FORM
        .QUICK_TIMEFRAME_OPTION_VALUE_3,
    "180":
      STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS.FORM
        .QUICK_TIMEFRAME_OPTION_VALUE_4,
    "365":
      STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS.FORM
        .QUICK_TIMEFRAME_OPTION_VALUE_5,
  };

  const label = timeframeLabelMap[timeframe];
  const normalizedLabel = label
    ? label.charAt(0).toLowerCase() + label.slice(1)
    : "";

  const renderTable = () => (
    <>
      {/* BEGIN - PERSONAL APPLICATIONS TABLE WIDGET */}
      <div className="[ row ]">
        <div className="[ col-xl-12 col-lg-12 ] [ main-container__members-column ]">
          <section
            className="branch-widget branch-widget--applications"
            aria-labelledby="personal-applications-section-title"
          >
            <Card className="branch-widget__card">
              <Card.Body>
                {/* Header */}
                <div className="branch-widget__header">
                  <div className="branch-widget__header-left">
                    <i
                      className="fas fa-file-alt branch-widget__icon"
                      aria-hidden="true"
                    ></i>
                    <h2
                      id="personal-applications-section-title"
                      className="branch-widget__title"
                    >
                      {
                        STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS.TABLE
                          .TITLE
                      }
                    </h2>
                  </div>
                </div>

                {/* Subheader */}
                <div className="branch-widget__table-header">
                  <div className="row">
                    <div className="[ col-12 ] [ d-flex justify-content-between align-items-center flex-wrap ]">
                      <span
                        ref={applicationTableHeaderRef}
                        className="branch-widget__table-title"
                        aria-live="polite"
                      >
                        {!hasApplicationSearched
                          ? ""
                          : applicationSearchResults.length > 0
                          ? `${STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS.TABLE.DISPLAYING_RESULTS} ${normalizedLabel}`
                          : STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS
                              .TABLE.NO_MATCHING}
                      </span>
                      {hasApplicationSearched && (
                        <Button
                          variant="light"
                          size="sm"
                          className="branch-widget__refresh branch-widget__refresh--icon"
                          aria-label="Refresh applications table"
                          onClick={handleApplicationSearch}
                        >
                          <i className="fas fa-sync" aria-hidden="true"></i>
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Status Filter Buttons */}
                {setApplicationStatus && (
                  <div
                    className="mb-3 d-flex flex-wrap"
                    style={{ gap: "0.5rem" }}
                  >
                    {["all", "inprogress", "inreview", "completed"].map((v) => (
                      <Button
                        key={v}
                        variant={
                          applicationStatus === v ? "dark" : "outline-secondary"
                        }
                        size="sm"
                        onClick={() => setApplicationStatus(v)}
                      >
                        {v === "all"
                          ? "All"
                          : v === "inprogress"
                          ? "In Progress"
                          : v === "inreview"
                          ? "In Review"
                          : "Completed"}
                      </Button>
                    ))}
                  </div>
                )}

                {/* Info Banner */}
                {hasApplicationSearched && applicationSearchResults.length > 0 && (
                  <div className="branch-widget__banner branch-widget__banner--info">
                    <p className="branch-widget__banner-text">
                      {
                        STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS.TABLE
                          .BANNER_INFO
                      }
                    </p>
                  </div>
                )}

                {/* Pagination - Top */}
                <div className="branch-widget__pagination d-flex align-items-center justify-content-between">
                  <div className="branch-widget__pagination-left d-flex align-items-center">
                    <label
                      htmlFor="app-itemsPerPage-top"
                      className="branch-widget__pagination-label"
                    >
                      Items per page
                    </label>
                    <select
                      id="app-itemsPerPage-top"
                      className="branch-widget__pagination-select form-select form-select-sm"
                      value={applicationSearchPagination.pageSize}
                      onChange={(e) => {
                        const newSize = parseInt(e.target.value, 10);
                        setApplicationSearchPagination((prev) => ({
                          ...prev,
                          pageSize: newSize,
                          currentPage: 0,
                        }));
                      }}
                    >
                      <option value={20}>20</option>
                      <option value={40}>40</option>
                    </select>
                  </div>
                  <div className="branch-widget__pagination-right d-flex align-items-center">
                    <label
                      htmlFor="app-pageSelect-top"
                      className="branch-widget__pagination-label"
                    >
                      Page
                    </label>
                    <select
                      id="app-pageSelect-top"
                      className="branch-widget__pagination-select form-select form-select-sm"
                      value={applicationSearchPagination.currentPage + 1}
                      onChange={(e) => {
                        const newPage = parseInt(e.target.value, 10) - 1;
                        setApplicationSearchPagination((prev) => ({
                          ...prev,
                          currentPage: newPage,
                        }));
                      }}
                    >
                      {Array.from(
                        {
                          length: Math.max(
                            1,
                            applicationSearchPagination.totalPages || 1
                          ),
                        },
                        (_, i) => (
                          <option key={i + 1} value={i + 1}>
                            {i + 1}
                          </option>
                        )
                      )}
                    </select>
                    <span className="branch-widget__pagination-info">
                      {STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE.PAGE_OF}{" "}
                      {applicationSearchPagination.totalPages || 1}
                    </span>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      aria-label={
                        STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE
                          .PREV_PAGE
                      }
                      className="branch-widget__pagination-btn"
                      disabled={applicationSearchPagination.currentPage === 0}
                      onClick={() =>
                        setApplicationSearchPagination((p) => ({
                          ...p,
                          currentPage: p.currentPage - 1,
                        }))
                      }
                    >
                      <i className="fas fa-chevron-left" aria-hidden="true"></i>
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      aria-label={
                        STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE
                          .NEXT_PAGE
                      }
                      className="branch-widget__pagination-btn"
                      disabled={
                        applicationSearchPagination.currentPage >=
                        (applicationSearchPagination.totalPages || 1) - 1
                      }
                      onClick={() =>
                        setApplicationSearchPagination((p) => ({
                          ...p,
                          currentPage: p.currentPage + 1,
                        }))
                      }
                    >
                      <i
                        className="fas fa-chevron-right"
                        aria-hidden="true"
                      ></i>
                    </Button>
                  </div>
                </div>

                {/* Table */}
                <Row>
                  <Col xs={12}>
                    <Table
                      striped
                      hover
                      size="sm"
                      responsive
                      className="branch-widget__table"
                      aria-label="Personal Applications Search Results"
                    >
                      <thead>
                        <tr>
                          <ApplicationSortableHeader
                            field="type"
                            label="PRODUCT"
                          />
                          <ApplicationSortableHeader
                            field="bundleProductId"
                            label="PRODUCT ID"
                          />
                          <ApplicationSortableHeader
                            field="id"
                            label="APPLICATION ID"
                          />
                          <ApplicationSortableHeader
                            field="applicantId"
                            label="APPLICANT ID"
                          />
                          <ApplicationSortableHeader
                            field="created"
                            label="TIME/DATE"
                          />
                          <ApplicationSortableHeader
                            field="status"
                            label="STATUS"
                          />
                          <th scope="col">View</th>
                        </tr>
                      </thead>
                      <tbody>
                        {applicationSearchLoading ? (
                          <tr>
                            <td
                              colSpan={7}
                              className="text-center text-muted py-4"
                            >
                              <div
                                role="status"
                                aria-live="polite"
                                className="branch-widget__status branch-widget__status--loading"
                              >
                                <i
                                  className="fas fa-spinner fa-spin me-2"
                                  aria-hidden="true"
                                ></i>
                                Searching applications...
                              </div>
                            </td>
                          </tr>
                        ) : hasApplicationSearched &&
                          applicationSearchResults.length > 0 ? (
                          applicationSearchResults.map((result, idx) => {
                            const status = getApplicationStatus(result);
                            return (
                              <tr key={`${result.id}-${idx}`}>
                                <td>{result.type || ""}</td>
                                <td>{result.bundleProductId || ""}</td>
                                <td>{result.id || ""}</td>
                                <td>{result.applicantId || ""}</td>
                                <td>
                                  {formatApplicationDateTime(result.created)}
                                </td>
                                <td>
                                  <span
                                    className={status.className}
                                    style={{ fontWeight: 500 }}
                                  >
                                    {status.status}
                                  </span>
                                </td>
                                <td className="branch-widget__body-link">
                                  <a
                                    href="#"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      if (handleViewMember) {
                                        handleViewMember(
                                          result.accountId,
                                          result.id
                                        );
                                      }
                                    }}
                                    aria-label={`View application ${result.id}`}
                                  >
                                    <i
                                      className="fas fa-external-link-alt"
                                      aria-hidden="true"
                                    ></i>
                                  </a>
                                </td>
                              </tr>
                            );
                          })
                        ) : !hasApplicationSearched ? (
                          <tr>
                            <td
                              colSpan={7}
                              className="text-center text-muted py-4"
                            >
                              <div className="branch-widget__message branch-widget__message--empty">
                                <div>
                                  Enter search criteria and click SEARCH to view
                                  applications.
                                </div>
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </tbody>
                    </Table>
                  </Col>
                </Row>

                {/* Pagination - Bottom */}
                {applicationSearchResults.length > 19 && (
                  <div className="branch-widget__pagination d-flex align-items-center justify-content-between">
                    <div className="branch-widget__pagination-left d-flex align-items-center">
                      <label
                        htmlFor="app-itemsPerPage-bottom"
                        className="branch-widget__pagination-label"
                      >
                        Items per page
                      </label>
                      <select
                        id="app-itemsPerPage-bottom"
                        className="branch-widget__pagination-select form-select form-select-sm"
                        value={applicationSearchPagination.pageSize}
                        onChange={(e) => {
                          const newSize = parseInt(e.target.value, 10);
                          setApplicationSearchPagination((prev) => ({
                            ...prev,
                            pageSize: newSize,
                            currentPage: 0,
                          }));
                        }}
                      >
                        <option value={20}>20</option>
                        <option value={40}>40</option>
                      </select>
                    </div>
                    <div className="branch-widget__pagination-right d-flex align-items-center">
                      <label
                        htmlFor="app-pageSelect-bottom"
                        className="branch-widget__pagination-label"
                      >
                        Page
                      </label>
                      <select
                        id="app-pageSelect-bottom"
                        className="branch-widget__pagination-select form-select form-select-sm"
                        value={applicationSearchPagination.currentPage + 1}
                        onChange={(e) => {
                          const newPage = parseInt(e.target.value, 10) - 1;
                          setApplicationSearchPagination((prev) => ({
                            ...prev,
                            currentPage: newPage,
                          }));
                        }}
                      >
                        {Array.from(
                          {
                            length: Math.max(
                              1,
                              applicationSearchPagination.totalPages || 1
                            ),
                          },
                          (_, i) => (
                            <option key={i + 1} value={i + 1}>
                              {i + 1}
                            </option>
                          )
                        )}
                      </select>
                      <span className="branch-widget__pagination-info">
                        {
                          STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE
                            .PAGE_OF
                        }{" "}
                        {applicationSearchPagination.totalPages || 1}
                      </span>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        aria-label={
                          STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE
                            .PREV_PAGE
                        }
                        className="branch-widget__pagination-btn"
                        disabled={applicationSearchPagination.currentPage === 0}
                        onClick={() =>
                          setApplicationSearchPagination((p) => ({
                            ...p,
                            currentPage: p.currentPage - 1,
                          }))
                        }
                      >
                        <i
                          className="fas fa-chevron-left"
                          aria-hidden="true"
                        ></i>
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        aria-label={
                          STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.TABLE
                            .NEXT_PAGE
                        }
                        className="branch-widget__pagination-btn"
                        disabled={
                          applicationSearchPagination.currentPage >=
                          (applicationSearchPagination.totalPages || 1) - 1
                        }
                        onClick={() =>
                          setApplicationSearchPagination((p) => ({
                            ...p,
                            currentPage: p.currentPage + 1,
                          }))
                        }
                      >
                        <i
                          className="fas fa-chevron-right"
                          aria-hidden="true"
                        ></i>
                      </Button>
                    </div>
                  </div>
                )}

                {/* Bottom Banner */}
                {hasApplicationSearched && (
                  <div className="branch-widget__banner branch-widget__banner--bottom">
                    {applicationSearchResults.length > 0 ? (
                      <div className="branch-widget__banner-text">
                        {
                          STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS.TABLE
                            .BANNER_BOTTOM_REVIEW
                        }
                      </div>
                    ) : (
                      <>
                        <div className="branch-widget__banner-text">
                          {
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS
                              .TABLE.BANNER_BOTTOM_NO_RESULTS1
                          }
                        </div>
                        <div className="branch-widget__banner-text">
                          {
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS
                              .TABLE.BANNER_BOTTOM_NO_RESULTS2
                          }
                        </div>
                      </>
                    )}
                  </div>
                )}
              </Card.Body>
            </Card>
          </section>
        </div>
      </div>
      {/* END - PERSONAL APPLICATIONS TABLE WIDGET */}
    </>
  );

  if (!showTableOnly) {
    return (
      <div className="[ row ]">
        <div className="[ col-xl-12 col-lg-12 col-12 ]">
          <section
            className="branch-widget branch-widget--applications-search"
            aria-labelledby="applications-search-title"
          >
            <Card className="branch-widget__card">
              <Card.Body>
                <h2 id="applications-search-title" className="sr-only">
                  {STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS.FORM.TITLE}
                </h2>
                {/* Toggle */}
                <div className="[ row ]">
                  <div className="col-12 d-flex justify-content-start">
                    <div className="[ mb-3 ]">
                      <button
                        type="button"
                        // ref={advancedToggleRef}
                        className="branch-widget__action invisible"
                        // onClick={toggleAdvanced}
                        // aria-expanded={showAdditionalFields}
                        // aria-controls="advanced-search-fields"
                        // aria-label={
                        //   STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                        //     .ADVANCED_SEARCH_TOGGLE
                        // }
                      >
                        <i
                          className="fas fa-sliders-h mr-1"
                          aria-hidden="true"
                        />{" "}
                        {
                          STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                            .ADVANCED_SEARCH_TOGGLE
                        }{" "}
                        {/* <i
                          className={
                            showAdditionalFields
                              ? "fas fa-caret-down ml-1"
                              : "fas fa-caret-right ml-1"
                          }
                          aria-hidden="true"
                        /> */}
                      </button>
                    </div>
                  </div>
                </div>

                <form
                  ref={applicationFormRef}
                  onSubmit={handleSubmit}
                  noValidate
                >
                  <div className="[ row ] align-items-end g-3">
                    {/* Application ID */}
                    <div className="[ col-12 col-lg-3 ]">
                      <div className="[ mb-3 ]">
                        <label htmlFor="applicationId" className="form-label">
                          {
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS.FORM
                              .APPLICATION_ID_LABEL
                          }
                        </label>
                        <input
                          className="form-control"
                          value={applicationId || ""}
                          onChange={handleApplicationIdChange}
                          onKeyDown={handleApplicationKeyDown}
                          maxLength={5}
                          placeholder={
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS.FORM
                              .APPLICATION_ID_PLACEHOLDER
                          }
                        />
                        <small id="middleName-hint" className="sr-only">
                          {
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                              .MIDDLENAME_HINT
                          }
                        </small>
                        <div className="[ error-msg ]" aria-live="polite"></div>
                      </div>
                    </div>

                    {/* Timeframe Quick Select */}
                    <div className="[ col-12 col-lg-3 ]">
                      <div className="[ mb-3 ]">
                        <label htmlFor="timeframe" className="form-label">
                          {
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS.FORM
                              .QUICK_TIMEFRAME_LABEL
                          }
                        </label>
                        <select
                          id="timeframe"
                          className="form-control"
                          value={timeframe}
                          onChange={handleTimeframeChange}
                        >
                          <option value={1}>
                            {
                              STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS
                                .FORM.QUICK_TIMEFRAME_OPTION_VALUE_0
                            }
                          </option>
                          <option value={7}>
                            {
                              STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS
                                .FORM.QUICK_TIMEFRAME_OPTION_VALUE_1
                            }
                          </option>
                          <option value={30}>
                            {
                              STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS
                                .FORM.QUICK_TIMEFRAME_OPTION_VALUE_2
                            }
                          </option>
                          <option value={90}>
                            {
                              STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS
                                .FORM.QUICK_TIMEFRAME_OPTION_VALUE_3
                            }
                          </option>
                          <option value={180}>
                            {
                              STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS
                                .FORM.QUICK_TIMEFRAME_OPTION_VALUE_4
                            }
                          </option>
                          <option value={365}>
                            {
                              STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS
                                .FORM.QUICK_TIMEFRAME_OPTION_VALUE_5
                            }
                          </option>
                          {/* <option value={0}>{STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS.FORM.QUICK_TIMEFRAME_OPTION_VALUE_CUSTOM}</option> */}
                        </select>
                        <small id="middleName-hint" className="sr-only">
                          {
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                              .MIDDLENAME_HINT
                          }
                        </small>
                        <div className="[ error-msg ]" aria-live="polite"></div>
                      </div>
                    </div>

                    {/* From Date */}
                    <div className="[ col-12 col-lg-3 ]">
                      <div className="[ mb-3 ]">
                        <label className="form-label">
                          {
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS.FORM
                              .DATE_FROM_LABEL
                          }
                        </label>
                        <input
                          className="form-control"
                          value={fromDate}
                          disabled
                        />
                        <small id="middleName-hint" className="sr-only">
                          {
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                              .MIDDLENAME_HINT
                          }
                        </small>
                        <div className="[ error-msg ]" aria-live="polite"></div>
                      </div>
                    </div>

                    {/* To Date */}
                    <div className="[ col-12 col-lg-3 ]">
                      <div className="[ mb-3 ]">
                        <label className="form-label">
                          {
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS.FORM
                              .DATE_TO_LABEL
                          }
                        </label>
                        <input
                          className="form-control"
                          value={toDate}
                          disabled
                        />
                        <small id="middleName-hint" className="sr-only">
                          {
                            STRINGS.BRANCH.MEMBER_HUB.PERSONAL_MEMBERS.FORM
                              .MIDDLENAME_HINT
                          }
                        </small>
                        <div className="[ error-msg ]" aria-live="polite"></div>
                      </div>
                    </div>
                  </div>
                  <div className="divider"></div>

                  {/* Error + Buttons */}
                  <div className="form-row mt-4 align-items-center justify-content-sm-end">
                    <div className="col-12 col-sm-auto mb-3 mb-sm-0">
                      {applicationSearchError && (
                        <div
                          id="application-search-error"
                          className="error-msg"
                          role="alert"
                          aria-live="assertive"
                        >
                          {applicationSearchError}
                        </div>
                      )}
                    </div>
                    <div className="col-12 col-sm-auto mb-3 mb-sm-0">
                      <button
                        type="reset"
                        className="btn btn-secondary me-2"
                        onClick={handleReset}
                      >
                        {
                          STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS.FORM
                            .RESET_BUTTON
                        }
                      </button>
                    </div>
                    <div className="col-12 col-sm-auto">
                      <button
                        type="submit"
                        className="btn btn-primary"
                        onKeyDown={handleApplicationKeyDown}
                      >
                        {
                          STRINGS.BRANCH.MEMBER_HUB.PERSONAL_APPLICATIONS.FORM
                            .SEARCH_BUTTON
                        }
                      </button>
                    </div>
                  </div>
                </form>
              </Card.Body>
            </Card>
          </section>
        </div>
      </div>
    );
  }

  return renderTable();
}
